import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, toRefs, Ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { convertFilePath, playSounds, stopSounds, soundIsPlaying } from '@/utilities'
import { useDeviceService, MediaType } from '@/composition/useDevice'
import { useProjectStore } from '@/store/useProjectStore'
import { useGameStore } from '@/store/useGameStore'
import { useAppStore } from '@/store/useAppStore'
import { Retell, RetellPage, TASK_TYPES } from '@/templates/slplus/structure/questionModels'
import { Player, TRACKING_TYPE, TrackingSearch, Sett, DialogConfig } from '@/types/main'
import Button from '@/components/base/Button.vue'
import RecordButton from '@/components/base/RecordButton.vue'
import { Carousel, Slide } from '@/lib/vue3-carousel/src'
import CarouselTrigger from '@/components/task/CarouselTrigger.vue'
import { TrackingDetails } from '@/types/main'

type Timer = ReturnType<typeof setTimeout>

enum MODES {
  intro = 'intro',
  playerReady = 'playerReady',
  instruction = 'instruction',
  sequence = 'sequence',
  tell = 'tell',
  yourTurn = 'yourTurn',
  retell = 'retell',
  videoname = 'videoname',
  finished = 'finished',

  // Recording
  stop = 'stop',
  record = 'record',
  play = 'play',
  delete = 'delete',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RetellDetail',
  props: {
  sett: {
    type: Object as PropType<Sett>,
    required: true,
  },
  question: {
    type: Object as PropType<Retell>,
    required: true,
  },
  player: {
    type: Object as PropType<Player>,
    default: undefined,
  },
  mode: {
    type: String as PropType<MODES>,
    default: 'tell',
  },
},
  emits: ['pageupdate'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    listenAgain: 'Hør igjen',
    dialogDeleteTitle: 'Slett denne lydopptak',
    dialogDeleteText: 'Hvis du sletter, du kan ikke angre',
    dialogDeleteConfirm: 'Slette',
    dialogDeleteCancel: 'Avbryt',
  },
  en: {
    listenAgain: 'Listen again',
    dialogDeleteTitle: 'Delete this audio recording',
    dialogDeleteText: 'Deletion cannot be undone',
    dialogDeleteConfirm: 'Delete',
    dialogDeleteCancel: 'Cancel',
  },
}




const { sett, question, mode, player } = toRefs(props)
const { t } = useI18n({ messages })
const { getters: deviceGetters, actions: deviceActions } = useDeviceService()
const { getters: gameGetters, actions: gameActions } = useGameStore()
const { getters: projectGetters } = useProjectStore()
const { actions: appActions } = useAppStore()
const omarActive = ref(false)
const currentPageIndex = ref(0)
const carouselTriggerRef = ref()
const currentPage: Ref<RetellPage> = ref(question.value.pages[currentPageIndex.value])
let currentPageAudio: HTMLAudioElement
const isRecording = ref(false)
const isPlaying = ref(false)
const recordingExists = ref(false)
let thisPageTracking: TrackingDetails | undefined
let soundTimeout: Timer

onMounted(() => {
  if (mode.value === MODES.retell) getUserRecording()
  else startPlaying()
})

const newPage = (index: number) => {
  clearTimeout(soundTimeout)
  stopSounds()
  if (thisPageTracking) {
    thisPageTracking.complete()
    gameActions.commitNewTracking(thisPageTracking)
  }
  currentPageIndex.value = index
  recordingExists.value = false
  currentPage.value = props.question.pages[currentPageIndex.value]
  emit('pageupdate', index)
  stopPlaying()
  if (mode.value === MODES.tell && currentPage.value.sound) {
    soundTimeout = setTimeout(() => {
      startPlaying()
    }, 500)
  } else if (mode.value === MODES.retell) getUserRecording()
}

const next = () => {
  carouselTriggerRef.value.next()
}
const prev = () => {
  if (carouselTriggerRef.value) carouselTriggerRef.value.prev()
}
const again = () => {
  if (carouselTriggerRef.value) carouselTriggerRef.value.slideTo(0)
}

// Recording
const startRecording = () => {
  isRecording.value = true
  deviceActions.createAudio().then(async () => {
    await deviceActions.startRecordingAudio()
  })
}

const stopRecording = async () => {
  await deviceActions.stopRecordingAudio()
  isRecording.value = false
  if (deviceGetters.audioFilename.value) {
    recordingExists.value = true
    if (thisPageTracking) {
      thisPageTracking.audioFile = deviceGetters.audioFilename.value
      thisPageTracking.isMedia = true
    }
  }
}

const startPlaying = () => {
  if (isPlaying.value) return
  let path = ''
  const media = deviceGetters.currentAudioFile.value
  if (mode.value === MODES.tell && currentPage.value.sound) {
    activateOmar()
  } else if (media) {
    path = convertFilePath(media.nativeURL)
    currentPageAudio = new Audio(path)
    currentPageAudio.addEventListener('ended', () => (omarActive.value = false))
    currentPageAudio.play()
    isPlaying.value = true

    currentPageAudio.onended = () => {
      isPlaying.value = false
    }
  }
}

const stopPlaying = () => {
  stopSounds()
  if (currentPageAudio) {
    currentPageAudio.pause()
    currentPageAudio.currentTime = 0
    isPlaying.value = false
  }
}

const deleteRecordedAudio = () => {
  if (deviceGetters.audioFilename.value) recordingExists.value = false
  const media = deviceGetters.audioFilename.value
  const dialogConfig: DialogConfig = {
    title: t('dialogDeleteTitle'),
    visible: true,
    text: t('dialogDeleteText'),
    cancel: () => appActions.setDialog(false),
    cancelText: t('dialogDeleteCancel'),
    confirm: () => {
      appActions.setDialog(false)
      if (media) {
        deviceActions.removeMedia(media, MediaType.audio).then(() => {
          recordingExists.value = false
        })
      }
    },
    confirmText: t('dialogDeleteConfirm'),
  }
  appActions.setDialog(true, dialogConfig)
}

function recordButtonClicked(mode: MODES) {
  switch (mode) {
    case MODES.stop:
      if (isPlaying.value) stopPlaying()
      else stopRecording()
      break
    case MODES.play:
      startPlaying()
      break
    case MODES.record:
      startRecording()
      break
    default:
      break
  }
}

// Make a tracking to represent this page's recording
const createTracking = () => {
  const game = gameGetters.selectedGame.value
  const project = projectGetters.selectedProject.value
  if (game && project && player?.value && question.value) {
    thisPageTracking = new TrackingDetails({
      itemID: `${sett.value._id}-${question.value._id}`,
      gameID: game._id,
      playerIDs: player.value ? [player.value._id] : ['player ID unknown'],
      projectID: project._id,
      type: TRACKING_TYPE.question,
      details: {
        taskType: TASK_TYPES.retell,
        taskInfo: question.value.name,
        page: currentPageIndex.value,
      },
    })
  }
}

function getUserRecording() {
  const search: TrackingSearch = {
    gameID: gameGetters.selectedGame.value?._id || '',
    itemID: `${sett.value._id}-${question.value._id}`,
    isMedia: true, // Only find Trackings with attached audio
  }
  const trackings = gameActions.findMatchingTrackings(search) as TrackingDetails[]
  // gameActions.findMatchingTrackings() returns an array reverse-sorted by date, so the first matching value will be the newest
  // Array.find() returns the first matching value
  const t = trackings.find((t) => t.details && t.details['page'] === currentPageIndex.value)
  thisPageTracking = new TrackingDetails(t)
  if (t && thisPageTracking && thisPageTracking.audioFile) {
    deviceActions.loadMedia(thisPageTracking.audioFile, MediaType.audio).then(() => {
      recordingExists.value = true
      // setTimeout(() => startPlaying(), 1000)
    })
  } else if (!t) {
    createTracking()
  }
}

function activateOmar() {
  omarActive.value = true
  if (currentPage.value.sound) {
    playSounds([currentPage.value.sound], () => (omarActive.value = false))
  } else omarActive.value = false
}

function startTouch() {
  console.log('touchstart')
}

const __returned__ = { MODES, messages, props, emit, sett, question, mode, player, t, deviceGetters, deviceActions, gameGetters, gameActions, projectGetters, appActions, omarActive, currentPageIndex, carouselTriggerRef, currentPage, currentPageAudio, isRecording, isPlaying, recordingExists, thisPageTracking, soundTimeout, newPage, next, prev, again, startRecording, stopRecording, startPlaying, stopPlaying, deleteRecordedAudio, recordButtonClicked, createTracking, getUserRecording, activateOmar, startTouch, stopSounds, soundIsPlaying, Button, RecordButton, Carousel, Slide, CarouselTrigger }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})